import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  useTheme,
  useMediaQuery,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { logEvent } from 'firebase/analytics';
import { analytics, db } from '../config/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { Header } from '../components/Header';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const MotionCard = motion(Card);

export const Signup: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobileNumber: '',
    password: '',
    confirmPassword: '',
    xmAccountNumber: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [affiliationStatus, setAffiliationStatus] = useState<'unchecked' | 'checking' | 'affiliated' | 'not-affiliated' | 'error'>('unchecked');
  const [affiliationMessage, setAffiliationMessage] = useState('');
  const [uniquenessStatus, setUniquenessStatus] = useState<'unchecked' | 'checking' | 'unique' | 'duplicate' | 'error'>('unchecked');
  const [uniquenessMessage, setUniquenessMessage] = useState('');
  const [xmInfoDialogOpen, setXmInfoDialogOpen] = useState(false);

  const { signup } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Track page view on component mount
  useEffect(() => {
    if (analytics) {
      logEvent(analytics, 'page_view', {
        page_title: 'Signup',
        page_location: window.location.href,
        page_path: '/signup'
      });
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    
    // Reset validation status when XM account number changes
    if (e.target.name === 'xmAccountNumber') {
      setAffiliationStatus('unchecked');
      setAffiliationMessage('');
      setUniquenessStatus('unchecked');
      setUniquenessMessage('');
    }
  };

  const checkXMAccountUniqueness = async (xmAccountNumber: string): Promise<boolean> => {
    try {
      // Track uniqueness check start
      if (analytics) {
        logEvent(analytics, 'xm_uniqueness_check_start', {
          account_number_length: xmAccountNumber.trim().length,
          device_type: isMobile ? 'mobile' : 'desktop'
        });
      }

      setUniquenessStatus('checking');
      setUniquenessMessage('🔍 Checking if XM account is already registered...');

      // Query Firestore to check if XM account number already exists
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('xmAccountNumber', '==', xmAccountNumber.trim()));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        // No existing user with this XM account number
        setUniquenessStatus('unique');
        setUniquenessMessage(''); // Don't show message when unique
        
        // Track successful uniqueness check
        if (analytics) {
          logEvent(analytics, 'xm_uniqueness_check_success', {
            is_unique: true,
            account_number_length: xmAccountNumber.trim().length,
            device_type: isMobile ? 'mobile' : 'desktop'
          });
        }
        
        return true;
      } else {
        // XM account number already exists
        setUniquenessStatus('duplicate');
        setUniquenessMessage('❌ This XM account number is already registered by another user');
        
        // Track duplicate XM account
        if (analytics) {
          logEvent(analytics, 'xm_uniqueness_check_success', {
            is_unique: false,
            account_number_length: xmAccountNumber.trim().length,
            device_type: isMobile ? 'mobile' : 'desktop'
          });
        }
        
        return false;
      }
    } catch (error: any) {
      console.error('Error checking XM account uniqueness:', error);
      setUniquenessStatus('error');
      setUniquenessMessage('Error checking XM account availability');
      
      // Track uniqueness check error
      if (analytics) {
        logEvent(analytics, 'xm_uniqueness_check_error', {
          error_message: error.message || 'Unknown error',
          device_type: isMobile ? 'mobile' : 'desktop'
        });
      }
      
      return false;
    }
  };

  const handleCheckAffiliation = async () => {
    if (!formData.xmAccountNumber || formData.xmAccountNumber.trim() === '') {
      setAffiliationStatus('error');
      setAffiliationMessage('Please enter an XM account number first');
      
      // Track affiliation check attempt without account number
      if (analytics) {
        logEvent(analytics, 'affiliation_check_error', {
          error_type: 'missing_account_number',
          device_type: isMobile ? 'mobile' : 'desktop'
        });
      }
      return;
    }

    // Track affiliation check start
    if (analytics) {
      logEvent(analytics, 'affiliation_check_start', {
        account_number_length: formData.xmAccountNumber.trim().length,
        device_type: isMobile ? 'mobile' : 'desktop'
      });
    }

    setAffiliationStatus('checking');
    setAffiliationMessage('Checking affiliation...');
    try {
      const response = await fetch(
        `https://us-central1-t1-fx-team.cloudfunctions.net/checkAffiliation?accountNumber=${encodeURIComponent(formData.xmAccountNumber)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      const data = await response.json();
      if (response.ok) {
        if (data.isAffiliated) {
          setAffiliationStatus('affiliated');
          setAffiliationMessage('✅ Account is affiliated with T1 FX Team');
          
          // Track successful affiliation verification
          if (analytics) {
            logEvent(analytics, 'affiliation_check_success', {
              is_affiliated: true,
              account_number_length: formData.xmAccountNumber.trim().length,
              device_type: isMobile ? 'mobile' : 'desktop'
            });
          }

          // Now check if XM account number is unique
          const isUnique = await checkXMAccountUniqueness(formData.xmAccountNumber);
          if (!isUnique && uniquenessStatus === 'duplicate') {
            // Keep affiliation status as affiliated since affiliation is valid,
            // but uniqueness check will prevent signup
            // The uniqueness message will show the specific error
          }
        } else {
          setAffiliationStatus('not-affiliated');
          setAffiliationMessage('❌ Account is not affiliated with T1 FX Team');
          
          // Track non-affiliated account
          if (analytics) {
            logEvent(analytics, 'affiliation_check_success', {
              is_affiliated: false,
              account_number_length: formData.xmAccountNumber.trim().length,
              device_type: isMobile ? 'mobile' : 'desktop'
            });
          }
        }
      } else {
        setAffiliationStatus('error');
        setAffiliationMessage('Error checking affiliation: ' + (data.message || 'Unknown error'));
        
        // Track API error
        if (analytics) {
          logEvent(analytics, 'affiliation_check_error', {
            error_type: 'api_error',
            error_message: data.message || 'Unknown error',
            response_status: response.status,
            device_type: isMobile ? 'mobile' : 'desktop'
          });
        }
      }
    } catch (error: any) {
      setAffiliationStatus('error');
      setAffiliationMessage('Network error: Unable to check affiliation');
      
      // Track network error
      if (analytics) {
        logEvent(analytics, 'affiliation_check_error', {
          error_type: 'network_error',
          error_message: error.message || 'Network error',
          device_type: isMobile ? 'mobile' : 'desktop'
        });
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Track signup attempt start
    if (analytics) {
      logEvent(analytics, 'signup_attempt', {
        affiliation_verified: affiliationStatus === 'affiliated',
        uniqueness_verified: uniquenessStatus === 'unique',
        device_type: isMobile ? 'mobile' : 'desktop'
      });
    }

    const { firstName, lastName, email, mobileNumber, password, confirmPassword, xmAccountNumber } = formData;
    if (!firstName || !lastName || !email || !mobileNumber || !password || !confirmPassword) {
      setError('Please fill in all required fields');
      
      // Track validation error
      if (analytics) {
        logEvent(analytics, 'signup_validation_error', {
          error_type: 'missing_fields',
          missing_fields: [
            !firstName && 'firstName',
            !lastName && 'lastName', 
            !email && 'email',
            !mobileNumber && 'mobileNumber',
            !password && 'password',
            !confirmPassword && 'confirmPassword'
          ].filter(Boolean),
          device_type: isMobile ? 'mobile' : 'desktop'
        });
      }
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      
      // Track password mismatch error
      if (analytics) {
        logEvent(analytics, 'signup_validation_error', {
          error_type: 'password_mismatch',
          device_type: isMobile ? 'mobile' : 'desktop'
        });
      }
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      
      // Track password length error
      if (analytics) {
        logEvent(analytics, 'signup_validation_error', {
          error_type: 'password_too_short',
          password_length: password.length,
          device_type: isMobile ? 'mobile' : 'desktop'
        });
      }
      return;
    }
    const mobileRegex = /^[\+]?\d[\d\s\-\(\)]{9,}$/;
    if (!mobileRegex.test(mobileNumber)) {
      setError('Please enter a valid mobile number');
      
      // Track mobile number validation error
      if (analytics) {
        logEvent(analytics, 'signup_validation_error', {
          error_type: 'invalid_mobile_number',
          mobile_length: mobileNumber.length,
          device_type: isMobile ? 'mobile' : 'desktop'
        });
      }
      return;
    }
    
    // Only validate XM account affiliation if XM account number is provided
    if (xmAccountNumber && xmAccountNumber.trim()) {
      if (affiliationStatus !== 'affiliated') {
        setError('Please verify your XM account affiliation before signing up.');
        
        // Track affiliation verification error
        if (analytics) {
          logEvent(analytics, 'signup_validation_error', {
            error_type: 'affiliation_not_verified',
            affiliation_status: affiliationStatus,
            device_type: isMobile ? 'mobile' : 'desktop'
          });
        }
        return;
      }
      if (uniquenessStatus !== 'unique') {
        setError('Please ensure your XM account number is available and unique.');
        
        // Track uniqueness verification error
        if (analytics) {
          logEvent(analytics, 'signup_validation_error', {
            error_type: 'xm_account_not_unique',
            uniqueness_status: uniquenessStatus,
            device_type: isMobile ? 'mobile' : 'desktop'
          });
        }
        return;
      }
    }

    try {
      setError('');
      setLoading(true);
      const displayName = `${firstName} ${lastName}`;
      // Pass affiliation verification status - true if XM account is provided and verified, false if no XM account
      const isAffiliationVerified = !!(xmAccountNumber && xmAccountNumber.trim() && affiliationStatus === 'affiliated');
      await signup(email, password, displayName, mobileNumber, xmAccountNumber.trim() || '', isAffiliationVerified);
      
      // Track successful signup
      if (analytics) {
        logEvent(analytics, 'signup_success', {
          user_name: displayName,
          email_domain: email.split('@')[1],
          mobile_country_code: mobileNumber.startsWith('+') ? mobileNumber.substring(1, 4) : 'unknown',
          xm_account_length: xmAccountNumber.length,
          has_xm_account: !!(xmAccountNumber && xmAccountNumber.trim()),
          device_type: isMobile ? 'mobile' : 'desktop'
        });
      }
      
      navigate('/dashboard');
    } catch (error: any) {
      setError('Failed to create account: ' + (error.message || 'Unknown error'));
      
      // Track signup failure
      if (analytics) {
        logEvent(analytics, 'signup_error', {
          error_message: error.message || 'Unknown error',
          error_code: error.code || 'unknown',
          device_type: isMobile ? 'mobile' : 'desktop'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pt: { xs: 8, md: 10 },
          pb: 4,
          background: 'linear-gradient(135deg, rgba(0, 212, 170, 0.1) 0%, rgba(66, 165, 245, 0.1) 100%)',
        }}
      >
        <Container maxWidth="sm">
          <MotionCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            sx={{
              p: { xs: 2, md: 4 },
              backdropFilter: 'blur(10px)',
              backgroundColor: 'rgba(21, 26, 35, 0.95)',
            }}
          >
            <CardContent>
              <Typography
                variant={isMobile ? 'h4' : 'h3'}
                component="h1"
                textAlign="center"
                gutterBottom
                sx={{
                  background: 'linear-gradient(45deg, #00D4AA, #42A5F5)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 700,
                  mb: 3,
                }}
              >
                Join T1 FX Team
              </Typography>
              
              <Typography
                variant="body1"
                textAlign="center"
                color="text.secondary"
                sx={{ mb: 4 }}
              >
                Create your account to join us now!
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit}>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <TextField
                    fullWidth
                    label="First Name"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    autoFocus
                  />
                  
                  <TextField
                    fullWidth
                    label="Last Name"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </Box>
                
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  margin="normal"
                  required
                  sx={{ mb: 2 }}
                />
                
                <TextField
                  fullWidth
                  label="Mobile Number"
                  name="mobileNumber"
                  type="tel"
                  value={formData.mobileNumber}
                  onChange={handleChange}
                  margin="normal"
                  required
                  sx={{ mb: 2 }}
                  helperText="Enter your mobile number with country code"
                />
                
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  margin="normal"
                  required
                  sx={{ mb: 2 }}
                  helperText="Must be at least 6 characters"
                />
                
                <TextField
                  fullWidth
                  label="Confirm Password"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  margin="normal"
                  required
                  sx={{ mb: 3 }}
                />

                <TextField
                  fullWidth
                  label="XM Account Number"
                  name="xmAccountNumber"
                  value={formData.xmAccountNumber}
                  onChange={handleChange}
                  margin="normal"
                  sx={{ mb: 2 }}
                  placeholder="XM trading account nr (optional)"
                  helperText={affiliationMessage || 'Your XM trading account number (optional)'}
                  InputProps={{
                    endAdornment: (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isMobile ? (
                          <>
                            <IconButton 
                              size="small" 
                              aria-label="XM Info" 
                              sx={{ color: 'primary.main' }} 
                              onClick={() => {
                                // Track XM info dialog open
                                if (analytics) {
                                  logEvent(analytics, 'xm_info_dialog_open', {
                                    device_type: 'mobile'
                                  });
                                }
                                setXmInfoDialogOpen(true);
                              }}
                            >
                              <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                            <Dialog open={xmInfoDialogOpen} onClose={() => {
                              // Track XM info dialog close
                              if (analytics) {
                                logEvent(analytics, 'xm_info_dialog_close', {
                                  device_type: 'mobile'
                                });
                              }
                              setXmInfoDialogOpen(false);
                            }}>
                              <DialogTitle sx={{ background: 'linear-gradient(135deg, #232b39 80%, #1a222e 100%)', color: '#fff' }}>How to get an XM Account Number?</DialogTitle>
                              <DialogContent sx={{ background: 'linear-gradient(135deg, #232b39 80%, #1a222e 100%)', color: '#fff' }}>
                                <Box sx={{ maxWidth: 320 }}>
                                  <Typography gutterBottom sx={{ color: '#fff' }}>
                                    You can optionally provide an XM trading account to join the T1 FX Team and participate in our programs.
                                  </Typography>
                                  <Typography gutterBottom sx={{ color: '#00D4AA', fontWeight: 700 }}>
                                    <b>Partner Code:</b> <span style={{ color: '#FFD700', fontWeight: 700 }}>T1FXTEAM</span>
                                  </Typography>
                                  <Typography gutterBottom>
                                    <b>Step 1:</b> <a 
                                      href="https://affs.click/bwvBB" 
                                      target="_blank" 
                                      rel="noopener noreferrer" 
                                      style={{ color: '#42A5F5', fontWeight: 600, textDecoration: 'underline' }}
                                      onClick={() => {
                                        // Track XM account link click
                                        if (analytics) {
                                          logEvent(analytics, 'xm_account_link_click', {
                                            source: 'mobile_dialog',
                                            device_type: 'mobile'
                                          });
                                        }
                                      }}
                                    >
                                      Open XM Account
                                    </a>
                                  </Typography>
                                  <Typography gutterBottom sx={{ color: '#fff' }}>
                                    <b>Step 2:</b> Complete the registration process on XM. Your account number will be provided by XM after registration.
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: '#B2DFDB' }}>
                                    Make sure to use the partner code <b style={{ color: '#FFD700' }}>T1FXTEAM</b> during registration to be affiliated with our team.
                                  </Typography>
                                </Box>
                              </DialogContent>
                              <DialogActions sx={{ background: 'linear-gradient(135deg, #232b39 80%, #1a222e 100%)' }}>
                                <Button 
                                  onClick={() => {
                                    // Track XM info dialog close via button
                                    if (analytics) {
                                      logEvent(analytics, 'xm_info_dialog_close_button', {
                                        device_type: 'mobile'
                                      });
                                    }
                                    setXmInfoDialogOpen(false);
                                  }} 
                                  color="primary" 
                                  variant="contained"
                                >
                                  Close
                                </Button>
                              </DialogActions>
                            </Dialog>
                          </>
                        ) : (
                          <Tooltip
                            title={
                              <Box sx={{ maxWidth: 320 }}>
                                <Typography gutterBottom sx={{ color: '#fff' }}>
                                  You can optionally provide an XM trading account to join the T1 FX Team and participate in our programs.
                                </Typography>
                                <Typography gutterBottom sx={{ color: '#00D4AA', fontWeight: 700 }}>
                                  <b>Partner Code:</b> <span style={{ color: '#FFD700', fontWeight: 700 }}>T1FXTEAM</span>
                                </Typography>
                                <Typography gutterBottom>
                                  <b>Step 1:</b> <a 
                                    href="https://affs.click/bwvBB" 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    style={{ color: '#42A5F5', fontWeight: 600, textDecoration: 'underline' }}
                                    onClick={() => {
                                      // Track XM account link click
                                      if (analytics) {
                                        logEvent(analytics, 'xm_account_link_click', {
                                          source: 'tooltip',
                                          device_type: 'desktop'
                                        });
                                      }
                                    }}
                                  >
                                    Open XM Account
                                  </a>
                                </Typography>
                                <Typography gutterBottom sx={{ color: '#fff' }}>
                                  <b>Step 2:</b> Complete the registration process on XM. Your account number will be provided by XM after registration.
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#B2DFDB' }}>
                                  Make sure to use the partner code <b style={{ color: '#FFD700' }}>T1FXTEAM</b> during registration to be affiliated with our team.
                                </Typography>
                              </Box>
                            }
                            arrow
                            placement="top"
                            onOpen={() => {
                              // Track tooltip open
                              if (analytics) {
                                logEvent(analytics, 'xm_info_tooltip_open', {
                                  device_type: 'desktop'
                                });
                              }
                            }}
                            slotProps={{
                              popper: {
                                sx: {
                                  '& .MuiTooltip-tooltip': {
                                    background: 'linear-gradient(135deg, #232b39 80%, #1a222e 100%)',
                                    color: '#fff',
                                    borderRadius: 2,
                                    boxShadow: '0 4px 24px 0 rgba(0,0,0,0.25)',
                                    fontSize: '1rem',
                                    p: 2,
                                    maxWidth: 320,
                                  },
                                  '& .MuiTooltip-arrow': {
                                    color: '#232b39',
                                  },
                                },
                              },
                            }}
                            enterTouchDelay={0}
                            disableHoverListener={isMobile}
                            disableFocusListener={isMobile}
                            disableTouchListener={!isMobile}
                          >
                            <IconButton size="small" aria-label="XM Info" sx={{ color: 'primary.main' }}>
                              <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {affiliationStatus === 'affiliated' && (
                          <Typography color="success.main" fontWeight={700}>✔</Typography>
                        )}
                        {(affiliationStatus === 'not-affiliated' || affiliationStatus === 'error' || uniquenessStatus === 'duplicate') && (
                          <Typography color="error.main" fontWeight={700}>✖</Typography>
                        )}
                        <Button
                          size="small"
                          onClick={handleCheckAffiliation}
                          disabled={(affiliationStatus === 'checking' || uniquenessStatus === 'checking') || !formData.xmAccountNumber.trim()}
                          sx={{ minWidth: 'auto', px: 1 }}
                        >
                          {(affiliationStatus === 'checking' || uniquenessStatus === 'checking') ? 'Checking...' : 'Check'}
                        </Button>
                      </Box>
                    ),
                  }}
                  FormHelperTextProps={{
                    sx: {
                      color: affiliationStatus === 'affiliated' ? 'success.main' : 
                             (affiliationStatus === 'not-affiliated' || affiliationStatus === 'error') ? 'error.main' :
                             affiliationStatus === 'checking' ? 'info.main' : 'text.secondary'
                    }
                  }}
                />

                {/* XM Account Uniqueness Status */}
                {uniquenessMessage && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: 'block',
                      mt: -0.5,
                      mb: 2,
                      px: 1.75,
                      color: uniquenessStatus === 'duplicate' || uniquenessStatus === 'error' ? 'error.main' :
                             uniquenessStatus === 'checking' ? 'info.main' : 'text.secondary'
                    }}
                  >
                    {uniquenessMessage}
                  </Typography>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading || (!!formData.xmAccountNumber.trim() && (affiliationStatus !== 'affiliated' || uniquenessStatus !== 'unique'))}
                  sx={{
                    py: 1.5,
                    mb: 2,
                    background: 'linear-gradient(45deg, #00D4AA, #00A085)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #00A085, #007A63)',
                    },
                    '&:disabled': {
                      background: 'rgba(255, 255, 255, 0.12)',
                      color: 'rgba(255, 255, 255, 0.7)',
                      cursor: 'not-allowed',
                    },
                  }}
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </Button>

                <Box textAlign="center">
                  <Typography variant="body2" color="text.secondary">
                    Already have an account?{' '}
                    <Link
                      component={RouterLink}
                      to="/login"
                      color="primary"
                      sx={{ fontWeight: 600 }}
                      onClick={() => {
                        // Track navigation to login
                        if (analytics) {
                          logEvent(analytics, 'navigate_to_login', {
                            source: 'signup_page',
                            device_type: isMobile ? 'mobile' : 'desktop'
                          });
                        }
                      }}
                    >
                      Sign in here
                    </Link>
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </MotionCard>
        </Container>
      </Box>
    </>
  );
}; 